/**
* @license Apache-2.0
*
* Copyright (c) 2018 The Stdlib Authors.
*
* Licensed under the Apache License, Version 2.0 (the "License");
* you may not use this file except in compliance with the License.
* You may obtain a copy of the License at
*
*    http://www.apache.org/licenses/LICENSE-2.0
*
* Unless required by applicable law or agreed to in writing, software
* distributed under the License is distributed on an "AS IS" BASIS,
* WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
* See the License for the specific language governing permissions and
* limitations under the License.
*/

'use strict';

// MODULES //

import resolveStr from '@stdlib/ndarray-base-dtype-resolve-str';
import bufferCtors from '@stdlib/ndarray-base-buffer-ctors';
import allocUnsafe from '@stdlib/buffer-alloc-unsafe';
import zeros from '@stdlib/array-base-zeros';
import fill from '@stdlib/array-base-fill';


// FUNCTIONS //

/**
* Returns a zero-filled binary buffer.
*
* @private
* @param {NonNegativeInteger} size - buffer size
* @returns {Buffer} zero-filled binary buffer
*/
function binary( size ) {
	return fill( allocUnsafe( size ), 0, 0, size );
}

/**
* Returns a typed array.
*
* @private
* @param {*} dtype - data type
* @param {NonNegativeInteger} size - buffer size
* @returns {(TypedArray|null)} typed array
*/
function typedarray( dtype, size ) {
	var ctor = bufferCtors( dtype );
	if ( ctor ) {
		return new ctor( size );
	}
	return null;
}


// MAIN //

/**
* Returns a contiguous linear ndarray data buffer.
*
* @param {*} dtype - data type
* @param {NonNegativeInteger} size - buffer size
* @returns {(Array|TypedArray|Buffer|null)} data buffer
*
* @example
* var buf = buffer( 'float64', 3 );
* // returns <Float64Array>[ 0.0, 0.0, 0.0 ]
*/
function buffer( dtype, size ) {
	var dt = resolveStr( dtype );
	if ( dt === 'generic' ) {
		return zeros( size );
	}
	if ( dt === 'binary' ) {
		return binary( size );
	}
	return typedarray( dtype, size );
}


// EXPORTS //

export default buffer;
